import React, { useEffect, useRef, useState } from "react";

import CheckIcon from "@material-ui/icons/Check";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";

import { endPoint } from "utils/config";
import logo from "assets/img/sparkLoginLogo.svg";
import "./authShell.css";

/* ==================================================================
   Shared parts of the public auth screens
   ------------------------------------------------------------------
   Sign in, sign up, phone verification and password recovery were six
   screens with six copies of the same password toggle, the same
   countdown and the same error handling — each with its own bugs. They
   share them from here instead.
   ================================================================== */

/**
 * The onboarding a new shop goes through. The screens in this folder
 * cover step 1; AddShop is step 2 and verification happens off-panel,
 * but the rail shows all four so the end is visible from the start.
 */
export const SIGNUP_STEPS = [
  { title: "Create your account", text: "Verify your phone and pick a password." },
  { title: "Tell us about your shop", text: "Name, address, hours and how you deliver." },
  { title: "Get verified", text: "We check your details before you go live." },
  { title: "Start selling", text: "Take orders through the Spark apps." },
];

/* ---------------- navigation ---------------- */

/**
 * Move between auth screens.
 *
 * These pages are mounted by real `<Route>`s, so they get a history
 * object and can navigate in-app. `endPoint` points at the deployed
 * panel, so the window.location fallback leaves the current origin —
 * only worth it when there is no router to fall back on.
 */
export const go = (history, path) => {
  if (history && typeof history.push === "function") {
    history.push(path);
    return;
  }
  window.location.href = endPoint + path;
};

/**
 * Leave the auth flow for the panel.
 *
 * Always a full page load: the Apollo client is built once at start-up
 * with whatever token was in localStorage at the time, so a token
 * obtained after that only takes effect on the next load.
 */
export const goAfterAuth = (path) => {
  window.location.href = endPoint + path;
};

/* ---------------- errors ---------------- */

export const codeOf = (error) =>
  error && error.graphQLErrors && error.graphQLErrors.length
    ? error.graphQLErrors[0].extensions && error.graphQLErrors[0].extensions.code
    : null;

/**
 * The message worth showing a person.
 *
 * The server sends readable sentences for the cases that matter
 * ("admin with this phone number exists"), so prefer those and only
 * fall back when the failure never reached it.
 */
export const readableError = (error) => {
  if (error && error.graphQLErrors && error.graphQLErrors.length) {
    return error.graphQLErrors[0].message;
  }
  if (error && error.networkError) {
    return "We could not reach the server. Check your connection and try again.";
  }
  return "Something went wrong. Please try again.";
};

/* ---------------- phone numbers ---------------- */

/**
 * Strip the punctuation people type into a phone field.
 *
 * The old form rejected anything `isNaN` said was not a number, which
 * turned away every "+1 (604) 555-0134" typed the way it is written.
 * The server only requires a non-empty string, so accept the format
 * and normalise it here.
 */
export const normalizePhone = (raw) => String(raw || "").replace(/[\s()\-.]/g, "");

export const isPlausiblePhone = (raw) => /^\+?\d{10,15}$/.test(normalizePhone(raw));

/* ---------------- verification-code timer ---------------- */

const DEADLINE_KEY = "codeDeadline";

/**
 * Remember when the code in flight stops working.
 *
 * The old screens stored the remaining seconds and rewrote them every
 * tick, so a closed tab froze the clock and reopening the page gave
 * back time the server had already taken. A deadline survives that.
 */
export const startCodeTimer = (expiresInSeconds) => {
  const seconds = Number(expiresInSeconds) || 0;
  window.localStorage.setItem(DEADLINE_KEY, String(Date.now() + seconds * 1000));
};

export const readCodeTimer = () => {
  const deadline = Number(window.localStorage.getItem(DEADLINE_KEY));
  if (!deadline) return 0;
  return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
};

export const clearCodeTimer = () => window.localStorage.removeItem(DEADLINE_KEY);

export const formatSeconds = (total) => {
  const safe = Math.max(0, Math.floor(total || 0));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

/**
 * A countdown that ticks to zero and stays there.
 *
 * One interval for the life of the component — the old screens created
 * one per render pass and never cleared it.
 */
export const useCountdown = (initialSeconds) => {
  const [remaining, setRemaining] = useState(Math.max(0, initialSeconds || 0));

  useEffect(() => {
    const id = setInterval(() => {
      setRemaining((current) => (current <= 1 ? 0 : current - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return [remaining, setRemaining];
};

/* ---------------- passwords ---------------- */

const STRENGTH_LABELS = ["weak", "fair", "good", "strong"];

/**
 * A rough score out of four, for the meter under the password field.
 *
 * The server's only rule is eight characters, so anything shorter
 * scores zero however varied it is — the meter should not call a
 * password the server will reject anything but too short.
 */
export const passwordScore = (password) => {
  const value = String(password || "");
  if (value.length < 8) return 0;

  let score = 1;
  if (value.length >= 12) score += 1;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
  if (/\d/.test(value) && /[^\w\s]/.test(value)) score += 1;
  return Math.min(4, score);
};

export const PasswordMeter = ({ password }) => {
  const score = passwordScore(password);
  const tone = score > 0 ? STRENGTH_LABELS[score - 1] : "weak";

  return (
    <div className="authMeter">
      <div className="authMeterTrack">
        {[0, 1, 2, 3].map((index) => (
          <span
            key={index}
            className={`authMeterBar ${index < score ? `isOn ${tone}` : ""}`}
          />
        ))}
      </div>
      <span className="authMeterLabel">
        {score === 0 ? "At least 8 characters" : tone}
      </span>
    </div>
  );
};

/**
 * A password input with a reveal toggle.
 *
 * The toggle sits inside the control so the field keeps the standard
 * .dsInput height and focus ring, and it is a real button so it is
 * reachable from the keyboard.
 */
export const PasswordInput = ({
  id,
  value,
  onChange,
  autoComplete = "current-password",
  autoFocus = false,
  disabled = false,
  placeholder,
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="authPasswordWrap">
      <input
        id={id}
        className="dsInput"
        type={visible ? "text" : "password"}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      <button
        type="button"
        className="authReveal"
        onClick={() => setVisible((current) => !current)}
        aria-label={visible ? "Hide password" : "Show password"}
        tabIndex={-1}
      >
        {visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </button>
    </div>
  );
};

/* ---------------- verification code input ---------------- */

/**
 * The five-digit box row for SMS codes.
 *
 * Codes are five digits (10000-99999). Handles paste, backspace across
 * boxes and arrow keys, because a code that has to be typed one digit
 * per box is only an improvement if it behaves like one field.
 */
export const CodeInput = ({
  value,
  onChange,
  onComplete,
  length = 5,
  error = false,
  disabled = false,
}) => {
  const boxes = useRef([]);

  const focusAt = (index) => {
    const box = boxes.current[index];
    if (box) {
      box.focus();
      box.select();
    }
  };

  const emit = (next) => {
    onChange(next);
    if (next.length === length && onComplete) onComplete(next);
  };

  const onBoxChange = (index, raw) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return;

    // Typing into a box replaces that one digit; pasting several fills
    // forward from wherever the caret was.
    const next = (value.slice(0, index) + digits + value.slice(index + 1)).slice(0, length);
    emit(next);
    focusAt(Math.min(length - 1, index + digits.length));
  };

  const onKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      if (value[index]) {
        emit(value.slice(0, index) + value.slice(index + 1));
      } else if (index > 0) {
        emit(value.slice(0, index - 1) + value.slice(index));
        focusAt(index - 1);
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusAt(index - 1);
    } else if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      focusAt(index + 1);
    }
  };

  const onPaste = (event) => {
    const digits = (event.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!digits) return;
    event.preventDefault();
    const next = digits.slice(0, length);
    emit(next);
    focusAt(Math.min(length - 1, next.length));
  };

  return (
    <div className={`authCode ${error ? "hasError" : ""}`}>
      {Array.from({ length }).map((unused, index) => (
        <input
          key={index}
          ref={(node) => {
            boxes.current[index] = node;
          }}
          className="authCodeBox"
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={length}
          disabled={disabled}
          autoFocus={index === 0}
          aria-label={`Digit ${index + 1}`}
          value={value[index] || ""}
          onChange={(event) => onBoxChange(index, event.target.value)}
          onKeyDown={(event) => onKeyDown(index, event)}
          onPaste={onPaste}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </div>
  );
};

/* ---------------- shell ---------------- */

/**
 * StepRail - where you are in setting a shop up.
 *
 * Lives on the dark brand panel by default; `onSurface` recolours it
 * for a page that shows the steps as its own content rather than as
 * background context.
 */
export const StepRail = ({ current, onSurface = false }) => (
  <ol className={`authSteps ${onSurface ? "onSurface" : ""}`}>
    {SIGNUP_STEPS.map((step, index) => {
      const state = index < current ? "isDone" : index === current ? "isCurrent" : "";
      return (
        <li key={step.title} className={`authStep ${state}`}>
          <span className="authStepMark">
            {index < current ? <CheckIcon /> : index + 1}
          </span>
          <span className="authStepBody">
            <strong>{step.title}</strong>
            <span>{step.text}</span>
          </span>
        </li>
      );
    })}
  </ol>
);

/**
 * AuthShell - the frame every public auth screen sits in.
 *
 * @param {Object} props
 * @param {string} props.title - Heading above the form
 * @param {string} props.subtitle - One line under the heading
 * @param {string} props.brandTitle - Headline on the brand panel
 * @param {string} props.brandText - Paragraph under it, when there is no step rail
 * @param {number} props.currentStep - Zero-based index into SIGNUP_STEPS; shows the rail
 * @param {React.ReactNode} props.children - The form
 * @param {React.ReactNode} props.footer - Small print under the form
 */
const AuthShell = ({
  title,
  subtitle,
  brandTitle = "Run your shop on Spark",
  brandText,
  currentStep,
  children,
  footer,
}) => (
  <div className="authPage">
    <aside className="authBrand">
      <div className="authBrandInner">
        <span className="authBrandLogo">
          <img src={logo} alt="Spark" />
        </span>
        <h2 className="authBrandTitle">{brandTitle}</h2>
        {typeof currentStep === "number" ? (
          <StepRail current={currentStep} />
        ) : (
          brandText && <p className="authBrandText">{brandText}</p>
        )}
      </div>
      <div className="authBrandFoot">Spark Apps</div>
    </aside>

    <main className="authMain">
      <div className="authCol">
        <div className="authMobileLogo">
          <img src={logo} alt="Spark" />
        </div>

        <div className="authHead">
          <h1 className="authTitle">{title}</h1>
          {subtitle && <p className="authSubtitle">{subtitle}</p>}
        </div>

        {children}

        {footer && <div className="authFoot">{footer}</div>}
      </div>
    </main>
  </div>
);

export default AuthShell;
